import React, { useEffect, useRef, useState } from 'react'
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonChip,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonRow,
  IonToast,
  IonToolbar,
} from '@ionic/react'
import MultisectionPicklist from '../../../components/multisectionPicklist/MultisectionPicklist.component'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { getSignatureOptionsSelector } from '../../options/selectors'
import { IOptionObj } from '../../../common/common.types'
import {
  addCustomSignature,
  customSignatureClearAll,
  loadCustomSignatureData,
  resetCustomSignatureClientData,
  saveTempSignature,
  setCurrentSelectedOption,
  setDeleteChipId,
  setSignatoryName,
  setSignatoryPhone,
  setSignatoryRelation,
  setSignedCustomSignature,
  setSigningReason,
} from '../store/customSignature.actions'
import { getCustomSignaturesSelector } from '../selectors/getCustomSignature.selectors'
import SignatureCanvasPad from '../../signatures/signatureCanvas/SignatureCanvasPad'
import { closeCircleOutline, lockClosed } from 'ionicons/icons'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import '../../signatures/page/Signatures.page.css'
import SInput from '../../../components/stringInput/StringInput.component'
import { blobToBase64, transformPhoneNumber } from '../../../utilities'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { IPrFormSignatureState } from '../store/customSignature.types'
import { useWindowSize } from '../../../hooks'
import { IAlertState } from '../../../components/alert/store/alert.types'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { Link } from 'react-router-dom'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import Validation from '../../../components/validation/Validation.component'
import { CustomSignaturesConstants } from '../store/customSignature.constants'
import { clearValidationErrorsByPathList } from '../../../utilities/runRulesValidation.util'
import { getRulesSelector } from '../../rules/selectors'
import { IRulesState } from '../../rules/store/rules.types'
import { ToastColor } from '../../../components/toast/store/toast.constants'
import { getToastProps } from '../../../components/toast/toastIcon.util'

const CustomSignatures: React.FC = () => {
  const { isTablet: isTabletDevice } = useDeviceMetrics()
  const rules: IRulesState = useSelector(getRulesSelector)
  const customSignaturesState = useSelector(getCustomSignaturesSelector)
  const { customSignatureClientData, prFormsSignature } = customSignaturesState
  const { currentSelectedOption, tempSignature, acknowledgePrivacyNotice, prFormsSignatureObj } =
    customSignatureClientData
  const { signingReason, signatoryName, signatoryRelation, signatoryPhone, formId, signatureId } = prFormsSignatureObj
  const signatureOptionsState = useSelector(getSignatureOptionsSelector)
  const { customSignatureTranformedOptions } = signatureOptionsState
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const canvasRef: any = useRef()
  const { width } = useWindowSize({ debounceMs: 300 })
  const [showToast, setShowToast] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [runValidationFlag, setRunValidationFlag] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const dispatchDataForValidation = () => {
    if (runValidationFlag) {
      dispatch(setSigningReason(signingReason))
      dispatch(setSignatoryName(signatoryName))
      dispatch(setSignatoryRelation(signatoryRelation))
      dispatch(setSignatoryPhone(signatoryPhone))
      setRunValidationFlag(false)
    }
  }

  useEffect(() => {
    dispatchDataForValidation()
  }, [runValidationFlag])

  useEffect(() => {
    setRunValidationFlag(true)
  }, [])

  const chooseSignatureType = !currentSelectedOption ? t('signature.chooseSignatureType') : ''
  const canvasContent = {
    canvasData: tempSignature,
    canvasHeader: currentSelectedOption ? currentSelectedOption.name + ' SIGNATURE' : '',
    canvasLink: !acknowledgePrivacyNotice && currentSelectedOption ? AppRouterConstants.TERMS_CONDITIONS_TAB : '',
    icon: lockClosed,
    canvasLabel: chooseSignatureType,
  }

  const isSignatureCanvasVisible = currentSelectedOption && acknowledgePrivacyNotice ? true : false

  const handleClearData = (selectedOption: IOptionObj) => {
    dispatch(resetCustomSignatureClientData())
    const signaturePresent = prFormsSignature.filter(
      (item) => item.formId === selectedOption.parentID && item.signatureId === selectedOption.id
    )
    if (signaturePresent.length > 0) {
      loadData(signaturePresent[0])
    } else {
      dispatch(setCurrentSelectedOption(selectedOption))
    }
  }

  const clearData = () => {
    rules.errors = clearValidationErrorsByPathList(rules.errors, ['state_customSignatures'])
    dispatch(customSignatureClearAll())
  }

  const handleClearAll = () => {
    const clearSigAlert: IAlertState = {
      header: t('signature.alert.clearSection.header'),
      message: t('signature.alert.clearSection.message'),
      buttons: [{ text: 'No' }, { text: 'Yes', role: 'destructive', handler: () => clearData() }],
    }
    dispatch(showAlert(clearSigAlert))
  }

  const checkSwitchSectionCondition = () => {
    if (signingReason || signatoryName || signatoryRelation || signatoryPhone || tempSignature) {
      return true
    } else {
      return false
    }
  }

  const setSelectedOption = (selectedOption: IOptionObj) => {
    if (checkSwitchSectionCondition()) {
      const changeSectionWarningAlert: IAlertState = {
        header: t('signature.alert.changeSectionWarning.header'),
        message: t('signature.alert.changeSectionWarning.message'),
        buttons: [{ text: 'No' }, { text: 'Yes', role: 'destructive', handler: () => handleClearData(selectedOption) }],
      }
      dispatch(showAlert(changeSectionWarningAlert))
    } else {
      const signaturePresent = prFormsSignature.filter(
        (item) => item.formId === selectedOption.parentID && item.signatureId === selectedOption.id
      )
      if (signaturePresent.length > 0) {
        loadData(signaturePresent[0])
      } else {
        dispatch(setCurrentSelectedOption(selectedOption))
      }
    }
  }

  const onDrawEnd = () => {
    if (canvasRef.current) {
      const base64 = canvasRef.current.getCanvas().toDataURL()
      dispatch(saveTempSignature(base64))
    }
    const ele: any = document.activeElement
    if (ele) ele?.blur()
  }

  const setSigningReasonOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setSigningReason(e.detail.value))
  }

  const setSignatoryNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setSignatoryName(e.detail.value))
  }

  const setSignatoryRelationOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setSignatoryRelation(e.detail.value))
  }

  const setSignatoryPhoneOnChange = (numbers: string[]) => {
    const newPhoneNumber = transformPhoneNumber(numbers.join(''))
    dispatch(setSignatoryPhone(newPhoneNumber))
  }

  const clearCanvas = () => {
    canvasRef.current && canvasRef.current.clear()
  }

  const saveSignature = () => {
    if (
      currentSelectedOption?.textInfoRequired === 1 &&
      (!signingReason || !signatoryName || !signatoryRelation || !signatoryPhone)
    ) {
      const emptyField = []
      if (!signingReason) {
        emptyField.push('Reason for Signing')
      }
      if (!signatoryName) {
        emptyField.push('Printed Name of Signatory')
      }
      if (!signatoryRelation) {
        emptyField.push('Relationship to Patient')
      }
      if (!signatoryPhone) {
        emptyField.push('Phone Number')
      }

      let alertMessage = ''
      emptyField.forEach((field) => {
        alertMessage = alertMessage + `${currentSelectedOption.name} ${field} Required </br>`
      })

      const requiredFields: IAlertState = {
        header: '',
        message: alertMessage,
      }
      dispatch(showAlert(requiredFields))
    } else {
      canvasRef.current.getCanvas().toBlob(async function (blob: Blob) {
        const data = await blobToBase64(blob)
        dispatch(setSignedCustomSignature(data.substring(data.indexOf(',') + 1)))
        dispatch(addCustomSignature())
        dispatch(saveTempSignature(''))
        clearCanvas()
        setShowToast(true)
        setTimeout(() => {
          setShowToast(false)
        }, 2000)
      })
    }
  }

  const loadCanvas = (blob: string) => {
    if (canvasRef.current) {
      const canvasWidth = canvasRef?.current?.getCanvas().width
      const canvasHeight = canvasRef?.current?.getCanvas().height
      clearCanvas()
      canvasRef.current.fromDataURL(blob, {
        width: canvasWidth,
        height: canvasHeight,
      })
    }
  }

  useEffect(() => {
    if (tempSignature) {
      loadCanvas(tempSignature)
    }
  }, [width, tempSignature, canvasRef.current, isExpanded])

  const FooterButtons = () => {
    return (
      <IonButtons className='ion-padding'>
        <IonButton
          className='btn-signature signature-button mr-8'
          color='danger'
          fill='outline'
          data-testid='ClearButton'
          onClick={() => handleClearAll()}
          disabled={prFormsSignature.length ? false : true}>
          {t('signature.clear')}
        </IonButton>
        <IonButton
          className='btn-signature signature-button btn-primary'
          data-testid='SaveSignatureButton'
          disabled={tempSignature ? false : true}
          onClick={saveSignature}>
          {t('signature.saveSignature')}
        </IonButton>
      </IonButtons>
    )
  }

  const loadData = (currentSelectedChip: IPrFormSignatureState) => {
    dispatch(loadCustomSignatureData(currentSelectedChip))
    dispatch(
      setCurrentSelectedOption({
        id: currentSelectedChip.signatureId,
        parentID: currentSelectedChip.formId,
        parentName: currentSelectedChip.__parentName,
        name: currentSelectedChip.__name,
        textInfoRequired: currentSelectedChip.__textInfoRequired,
      })
    )
    setRunValidationFlag(true)
  }

  const handleRemove = (signChip: IPrFormSignatureState) => {
    dispatch(
      setDeleteChipId({
        formId: signChip.formId,
        signatureId: signChip.signatureId,
      })
    )
  }

  const removeSignatureChip = (signChip: IPrFormSignatureState) => {
    const removeSigChip: IAlertState = {
      header: t('signature.alert.deleteAttachment.header'),
      message: t('signature.alert.deleteAttachment.message'),
      buttons: [{ text: 'No' }, { text: 'Yes', role: 'destructive', handler: () => handleRemove(signChip) }],
    }
    dispatch(showAlert(removeSigChip))
  }

  const onFocus = () => {
    setIsFocused(true)
  }

  const onBlur = () => {
    setIsFocused(false)
  }

  return (
    <>
      {showToast && (
        <IonToast
          isOpen={showToast}
          position='top'
          message={'Signature successfully attached'}
          {...getToastProps(ToastColor.Success)}
          duration={2000}
          onDidDismiss={() => setShowToast(false)}
        />
      )}
      <hr className='field-divider' />
      <IonContent fullscreen>
        <IonList className={`${isExpanded && 'h-100'}`}>
          <IonGrid className='no-padding'>
            <IonRow>
              <IonCol className='picklist'>
                <MultisectionPicklist
                  dataTestID='CustomSignatureSection'
                  label={prFormsSignature.length > 0 ? 'Add Another Signature' : 'Custom Signature Type'}
                  listHeader={'Custom Signatures:'}
                  options={customSignatureTranformedOptions}
                  currentSelectedOption={currentSelectedOption}
                  setSelectedOption={setSelectedOption}
                  selectedOptions={prFormsSignature}
                />
                <hr className='field-divider' />
              </IonCol>
            </IonRow>
          </IonGrid>

          <SignatureCanvasPad
            canvasRef={canvasRef}
            isCanvasVisible={isSignatureCanvasVisible}
            canvasContent={canvasContent}
            onClear={() => {
              dispatch(saveTempSignature())
            }}
            onEnd={onDrawEnd}
            canvasHeight={215}
            canvasWidth={width}
            isCanvasExpandable={true}
            onCanvasExpandChange={setIsExpanded}
          />

          {currentSelectedOption && (
            <>
              <IonRow>
                <IonCol className='picklist'>
                  <SInput
                    isPHI
                    label={t('signature.reasonForSigning')}
                    dataTestID='reasonForSigning'
                    inputValue={signingReason}
                    onChangeAction={setSigningReasonOnChange}
                    onFocusAction={onFocus}
                    onBlurAction={onBlur}
                  />
                  <Validation
                    id={CustomSignaturesConstants.SET_CUSTOM_SIGNATURE_SIGNING_REASON}
                    cardinalityKey={`${formId}${signatureId}`}
                    showReduxStateErrorsOnlyFlag={true}
                  />
                </IonCol>
              </IonRow>
              <hr className='field-divider' />
              <IonRow>
                <IonCol className='picklist'>
                  <SInput
                    isPHI
                    label={t('signature.printedName')}
                    dataTestID='printedName'
                    inputValue={signatoryName}
                    onChangeAction={setSignatoryNameOnChange}
                    onFocusAction={onFocus}
                    onBlurAction={onBlur}
                  />
                  <Validation
                    id={CustomSignaturesConstants.SET_CUSTOM_SIGNATURE_SIGNATORY_NAME}
                    cardinalityKey={`${formId}${signatureId}`}
                    showReduxStateErrorsOnlyFlag={true}
                  />
                </IonCol>
              </IonRow>
              <hr className='field-divider' />
              <IonRow>
                <IonCol className='picklist'>
                  <SInput
                    isPHI
                    label={t('signature.relationshipToPatient')}
                    dataTestID='relationshipToPatient'
                    inputValue={signatoryRelation}
                    onChangeAction={setSignatoryRelationOnChange}
                    onFocusAction={onFocus}
                    onBlurAction={onBlur}
                  />
                  <Validation
                    id={CustomSignaturesConstants.SET_CUSTOM_SIGNATURE_SIGNATORY_RELATION}
                    cardinalityKey={`${formId}${signatureId}`}
                    showReduxStateErrorsOnlyFlag={true}
                  />
                </IonCol>
              </IonRow>
              <hr className='field-divider' />
              <IonRow>
                <IonCol className='picklist'>
                  <NumberPad
                    isPHI
                    elementId={'PhoneNumber'}
                    dataTestId={'PhoneNumber'}
                    values={signatoryPhone.split('')}
                    label={t('signature.phoneNumber')}
                    onNumPadClick={(numbers) => {
                      setSignatoryPhoneOnChange(numbers)
                    }}
                    onFocus={onFocus}
                    onBlur={onBlur}
                  />
                  <Validation
                    id={CustomSignaturesConstants.SET_CUSTOM_SIGNATURE_SIGNATORY_PHONE}
                    cardinalityKey={`${formId}${signatureId}`}
                    showReduxStateErrorsOnlyFlag={true}
                  />
                </IonCol>
              </IonRow>
              <hr className='field-divider' />
            </>
          )}
          <IonRow>
            <IonCol className='landscape-signature-action'>{FooterButtons()}</IonCol>
          </IonRow>
          {isFocused && (
            <IonItemDivider
              color='light'
              className={isTabletDevice ? 'bottom-divider-tablet' : 'bottom-divider-mobile'}
            />
          )}
        </IonList>
      </IonContent>
      <IonFooter className='signature-footer'>
        <IonToolbar color='light'>
          {prFormsSignature.length > 0 && (
            <IonGrid className='padding-bottom'>
              <IonRow className='chip-heading'>Saved Custom Signatures</IonRow>
              <IonRow className='ion-nowrap no-flex-shrink'>
                {prFormsSignature.map((item: IPrFormSignatureState) => {
                  const name = item.__parentName + ' - ' + item.__name + ' ' + item.seq

                  return (
                    <IonChip key={name} onClick={() => loadData(item)} className='signature-chip'>
                      <IonLabel className=''>{name}</IonLabel>
                      <IonIcon
                        class='ion-align-self-center'
                        size='small'
                        icon={closeCircleOutline}
                        color='white'
                        onClick={() => {
                          removeSignatureChip(item)
                        }}
                      />
                    </IonChip>
                  )
                })}
              </IonRow>
            </IonGrid>
          )}
          {acknowledgePrivacyNotice && currentSelectedOption && (
            <IonRow className='ion-nowrap' color=''>
              <IonItem color='light' className='terms-checkbox-item'>
                <IonCheckbox
                  color='light'
                  disabled={true}
                  className='terms-checkbox'
                  slot='start'
                  checked={acknowledgePrivacyNotice}></IonCheckbox>
              </IonItem>
              <IonLabel className='terms-text'>
                Signatory agrees with{' '}
                <Link className='text-link' to={AppRouterConstants.TERMS_CONDITIONS_TAB}>
                  terms and conditions
                </Link>
              </IonLabel>
            </IonRow>
          )}
          {FooterButtons()}
        </IonToolbar>
      </IonFooter>
    </>
  )
}

export default CustomSignatures
